/**
 * Role-wise names for the Gate module.
 *
 * The same trucks read differently from each side of the barrier: to the guard a truck on Inward
 * Pending is waiting at the gate, to the arrival desk it has not arrived yet. So a role may rename
 * the module and its tabs (`roles.gate_labels`). Display only — URLs, tab codes and the API keep
 * their names.
 */
import { z } from "zod";
import { GATE_TABS, type GateTab } from "./gate";

export const GATE_LABEL_KEYS = ["module", ...GATE_TABS] as const;
export type GateLabelKey = "module" | GateTab;
export type GateLabels = Partial<Record<GateLabelKey, string>>;

export const GATE_LABEL_DEFAULTS: Record<GateLabelKey, string> = {
  module: "Gate",
  all: "All",
  inward_pending: "Inward Pending",
  inward_done: "Inward Done",
  outward_pending: "Outward Pending",
  outward_done: "Outward Done",
};

const labelName = z.string().trim().max(40, "Keep it to 40 characters");

/**
 * What an admin saves. A blank name means "use the default" and is dropped, so an emptied input
 * resets rather than blanking the rail.
 */
export const gateLabels = z
  .object(
    Object.fromEntries(
      GATE_LABEL_KEYS.map((k) => [k, labelName.optional()])
    ) as Record<GateLabelKey, z.ZodOptional<typeof labelName>>
  )
  .strict()
  .transform((o): GateLabels =>
    Object.fromEntries(
      Object.entries(o).filter(([, v]) => typeof v === "string" && v)
    )
  );

/**
 * The names one login sees, from the overrides of each of its roles that open the gate. A name is
 * overridden only when every such role gives it the same name: a desk user who is also a guard (or
 * an admin) keeps the standard names, never a mix that depends on role order.
 */
export function mergeGateLabels(perRole: readonly GateLabels[]): GateLabels {
  if (!perRole.length) return {};
  const out: GateLabels = {};
  for (const k of GATE_LABEL_KEYS) {
    const first = perRole[0]![k];
    if (first && perRole.every((r) => r[k] === first)) out[k] = first;
  }
  return out;
}

export const gateLabel = (
  labels: GateLabels | null | undefined,
  key: GateLabelKey
) => labels?.[key] || GATE_LABEL_DEFAULTS[key];

/**
 * Which of the five tabs a role shows (`roles.gate_tabs`). A role that hid every tab would open an
 * empty page, so at least one stays. Order follows the tab bar whatever order the admin ticked in.
 */
export const gateTabs = z
  .array(z.enum(GATE_TABS))
  .min(1, "Show at least one tab")
  .transform((tabs): GateTab[] => GATE_TABS.filter((t) => tabs.includes(t)));

/**
 * The tabs one login sees: a tab shows when ANY of its gate roles shows it, so a desk user who is
 * also a guard keeps the guard's tabs. No gate role (gate.view by a per-user grant) = every tab.
 */
export function mergeGateTabs(
  perRole: readonly (readonly string[])[]
): GateTab[] {
  if (!perRole.length) return [...GATE_TABS];
  return GATE_TABS.filter((t) => perRole.some((r) => r.includes(t)));
}

/** Where the gate opens: the guard's queue when this login has it, else its first tab. */
export const defaultGateTab = (visible: readonly GateTab[]): GateTab =>
  visible.includes("inward_pending")
    ? "inward_pending"
    : (visible[0] ?? "inward_pending");

/**
 * How a role that holds `gate.pass_in.manual` numbers a new Gate Pass In (`roles.pass_in_manual_mode`):
 * always the paper pass number, never, or the guard's pick with the switch (user, 2026-09-24).
 */
export const PASS_IN_MANUAL_MODES = ["always", "never", "choice"] as const;
export type PassInManualMode = (typeof PASS_IN_MANUAL_MODES)[number];
export const passInManualMode = z.enum(PASS_IN_MANUAL_MODES);

export const PASS_IN_MANUAL_MODE_LABELS: Record<PassInManualMode, string> = {
  always: "Always manual",
  never: "Never",
  choice: "User's choice",
};

/**
 * One login's mode, from the roles that grant it `gate.pass_in.manual`. The freest wins, as with
 * Gate tabs: a desk user who is also a guard keeps the desk's switch. No such role = never, unless
 * the code came by a per-user grant, which carries no setting and so leaves it to the user.
 */
export function mergePassInManualMode(
  perRole: readonly string[],
  hasCode: boolean
): PassInManualMode {
  if (!hasCode) return "never";
  if (!perRole.length || perRole.includes("choice")) return "choice";
  return perRole.includes("always") ? "always" : "never";
}
